// =============================================================================
// LOGO CLOUD SECTION COMPONENT (V2 - NORMALIZED)
// =============================================================================

import React from 'react';
import { ExternalLink } from 'lucide-react';
import { layoutToClasses, displayToStyles } from '@/types/layout';
import type { ContentComponent } from '@/types';
import type { LogoCloudComponent } from '@/types/components';

interface LogoCloudSectionProps {
  component: ContentComponent;
}

/**
 * Type guard for new LogoCloudComponent format
 */
function isNewLogoCloudFormat(component: ContentComponent): boolean {
  return (
    component.type === 'logo-cloud' &&
    'items' in component &&
    Array.isArray((component as any).items)
  );
}

/**
 * Logo Cloud Section Component
 */
export const LogoCloudSection: React.FC<LogoCloudSectionProps> = ({ component }) => {
  if (isNewLogoCloudFormat(component)) {
    return <LogoCloudSectionNew component={component as unknown as any} />;
  }
  return <LogoCloudSectionLegacy component={component} />;
};

/**
 * New format Logo Cloud Section
 */
const LogoCloudSectionNew: React.FC<{ component: LogoCloudComponent }> = ({ component }) => {
  const { id, content, items, layout, display } = component;
  
  const layoutClasses = layoutToClasses(layout);
  const displayStyles = displayToStyles(display);
  
  return (
    <section id={id} className={`content-section logo-cloud-section ${layoutClasses}`} style={displayStyles}>
      <div className="container">
        {content.title && (
          <div className="section-header">
            <h2>{content.title}</h2>
            {content.description && <p>{content.description}</p>}
          </div>
        )}
        
        <div className={`logo-cloud grid-cols-${layout.columns || 6}`}>
          {items.map((logo, index) => (
            <div key={logo.id || index} className="logo-item">
              {logo.url ? (
                <a
                  href={logo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="logo-link"
                  aria-label={`${logo.name} website`}
                >
                  <div className="logo-image">
                    <img src={logo.logo} alt={logo.alt || `${logo.name} logo`} loading="lazy" />
                  </div>
                  <div className="logo-overlay">
                    <ExternalLink size={16} />
                  </div>
                </a>
              ) : (
                <div className="logo-image">
                  <img src={logo.logo} alt={logo.alt || `${logo.name} logo`} loading="lazy" />
                </div>
              )}
            </div>
          ))}
        </div>
        
        {items.length === 0 && (
          <div className="logo-cloud-placeholder">
            <p>Add your client or partner logos to showcase your trusted relationships.</p>
          </div>
        )}
      </div>
    </section>
  );
};

/**
 * Legacy format Logo Cloud Section
 */
const LogoCloudSectionLegacy: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const logos = metadata?.logos || [];
  const columns = metadata?.columns || 6;
  
  return (
    <section id={id} className="content-section logo-cloud-section">
      <div className="container">
        {title && (
          <div className="section-header">
            <h2>{title}</h2>
            {content && <p>{content}</p>}
          </div>
        )}
        
        <div className={`logo-cloud grid-cols-${columns}`}>
          {logos.map((logo: any, index: number) => (
            <div key={index} className="logo-item">
              {logo.url ? (
                <a
                  href={logo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="logo-link"
                  aria-label={`${logo.name} website`}
                >
                  <div className="logo-image">
                    <img src={logo.logo} alt={logo.alt || `${logo.name} logo`} loading="lazy" />
                  </div>
                  <div className="logo-overlay">
                    <ExternalLink size={16} />
                  </div>
                </a>
              ) : (
                <div className="logo-image">
                  <img src={logo.logo} alt={logo.alt || `${logo.name} logo`} loading="lazy" />
                </div>
              )}
            </div>
          ))}
        </div>
        
        {logos.length === 0 && (
          <div className="logo-cloud-placeholder">
            <p>Add your client or partner logos to showcase your trusted relationships.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export { LogoCloudSectionNew, LogoCloudSectionLegacy };

